// Catálogo de TIPOS de video del Creador de video — tabla `video_tipos`
// (ADR-2026-09-18-el-creador-de-video-escribe-el-prompt-con-skills-por-tipo). Cada tipo
// tiene su skill en atlas-backend con el mismo slug; "General" no es una fila: es la
// ausencia de tipo, y el escritor elige.
//
// Tabla `video_tipos` (migración 20260918120000_video_tipos.sql):
//   id, slug, nombre, categoria, descripcion, orden, video_url, thumbnail_url, activo

import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

export interface TipoDeVideo {
  /** = video_tipos.slug = skill del backend. */
  slug: string;
  nombre: string;
  /** Rótulo tal como se muestra. Vacío en General. */
  categoria: string;
  descripcion: string;
  /** Clip 9:16 de ejemplo; sin él, la card muestra placeholder. */
  videoUrl?: string;
  posterUrl?: string;
}

export const GENERAL: TipoDeVideo = {
  slug: 'general',
  nombre: 'General',
  categoria: '',
  descripcion: 'Atlas elige el tipo de video según lo que pidas y lo que adjuntes.',
};

export const esGeneral = (slug: string): boolean => slug === GENERAL.slug;

/** La seed de la migración, para que un tipo nuevo obligue a tocar los tres lugares
 *  (migración, skill del backend, acá). `videoTiposCatalog.test.ts` lo verifica. */
export const SLUGS_ESPERADOS = [
  'resena',
  'unboxing',
  'probandomelo',
  'producto-heroe',
  'lifestyle-con-modelo',
  'lanzamiento',
  'transicion-de-outfit',
  'antes-y-despues',
] as const;

export function mapearFila(row: Record<string, unknown>): TipoDeVideo {
  return {
    slug: String(row.slug ?? ''),
    nombre: String(row.nombre ?? '') || 'Tipo de video',
    categoria: String(row.categoria ?? ''),
    descripcion: String(row.descripcion ?? ''),
    videoUrl: typeof row.video_url === 'string' && row.video_url ? row.video_url : undefined,
    posterUrl:
      typeof row.thumbnail_url === 'string' && row.thumbnail_url ? row.thumbnail_url : undefined,
  };
}

/** Grupos por categoría en orden de aparición (la consulta ya viene por `orden`). */
export function agruparPorCategoria(
  tipos: TipoDeVideo[],
): Array<{ categoria: string; tipos: TipoDeVideo[] }> {
  const grupos: Array<{ categoria: string; tipos: TipoDeVideo[] }> = [];
  for (const t of tipos) {
    if (esGeneral(t.slug)) continue;
    let g = grupos.find((x) => x.categoria === t.categoria);
    if (!g) {
      g = { categoria: t.categoria, tipos: [] };
      grupos.push(g);
    }
    g.tipos.push(t);
  }
  return grupos;
}

export async function fetchVideoTipos(): Promise<TipoDeVideo[]> {
  const { data, error } = await supabase
    .from('video_tipos')
    .select('slug, nombre, categoria, descripcion, video_url, thumbnail_url')
    .eq('activo', true)
    .order('orden');
  if (error || !data) {
    console.error('[videoTiposCatalog] fetchVideoTipos error', error);
    return [];
  }
  return (data as Record<string, unknown>[]).map(mapearFila);
}

// Caché de sesión: el catálogo es global y chico; no cambia mientras la app está abierta.
let cache: TipoDeVideo[] | null = null;
let enVuelo: Promise<TipoDeVideo[]> | null = null;

export function useVideoTipos(): { tipos: TipoDeVideo[]; cargando: boolean } {
  const [tipos, setTipos] = useState<TipoDeVideo[]>(cache ?? []);
  const [cargando, setCargando] = useState(cache == null);
  useEffect(() => {
    if (cache) return;
    let vivo = true;
    enVuelo ??= fetchVideoTipos().then((t) => {
      cache = t;
      return t;
    });
    enVuelo.then((t) => {
      if (!vivo) return;
      setTipos(t);
      setCargando(false);
    });
    return () => {
      vivo = false;
    };
  }, []);
  return { tipos, cargando };
}
